import { defineStore } from "pinia";
import { computed, inject, onMounted, ref, watch } from "vue";
import { useAutoExpiringQueue } from "@/common/composables/auto-expiring-queue";
export var ToastType;
(function (ToastType) {
    ToastType["BASE"] = "base";
    ToastType["SUCCESS"] = "success";
    ToastType["ERROR"] = "error";
    ToastType["DELETE"] = "delete";
    ToastType["FATAL_ERROR"] = "fatal_error";
})(ToastType || (ToastType = {}));
const defaultTimeout = 5000;
export const useToastNotificationStore = defineStore("toast-notifications", () => {
    const i18n = inject("i18n");
    const toastQueue = useAutoExpiringQueue((toast) => toast.options?.permanent ? null : toast.options?.timeout ?? defaultTimeout);
    const toastList = toastQueue.items;
    const hasPermanentToast = computed(() => toastList.value.some((toast) => toast.options?.permanent));
    const hovering = ref(false);
    // Pause when a permanent toast appears; resume when it is dismissed — unless the user
    // is still hovering, in which case toastHover(false) will resume on mouseout.
    watch(hasPermanentToast, (hasPermanent) => {
        if (hasPermanent) {
            toastQueue.pause();
        }
        else if (!hovering.value) {
            toastQueue.resume();
        }
    });
    const addToast = (type, message, options) => {
        const id = `toast-${Date.now()}-${Math.random()}`;
        toastQueue.add({ id, type, message, options });
        return id;
    };
    const addSuccessNotice = (message, options) => addToast(ToastType.SUCCESS, message, options);
    const addErrorNotice = (message, options) => addToast(ToastType.ERROR, message, options);
    const addDeleteNotice = (message, options) => addToast(ToastType.DELETE, message, options);
    const addFatalErrorNotice = (message, secondaryMessage, options) => {
        const svgIcon = options?.svgIcon || "icon-caution";
        return addToast(ToastType.FATAL_ERROR, message, { secondaryMessage, svgIcon, ...options });
    };
    const addFatalSaveErrorNotice = (error, message) => {
        const { statusCode } = error;
        if (!statusCode) {
            console.log("API error will be displayed as 400", error);
        }
        addFatalErrorNotice(`${i18n.t("Unable to save changes")} (${i18n.t("Error")} ${statusCode ?? 400})`, message ?? i18n.t("Please copy any unsaved changes and refresh the page."));
    };
    const toastHover = (hover) => {
        hovering.value = hover;
        if (hover) {
            toastQueue.pause();
        }
        else if (!hasPermanentToast.value) {
            toastQueue.resume();
        }
    };
    const toastClick = (toast) => {
        if (toast.options?.clickCallback) {
            toast.options.clickCallback();
        }
        closeToastById(toast.id);
    };
    const primaryAction = (toast) => {
        if (toast.options?.actionButtonCallback) {
            toast.options.actionButtonCallback();
            closeToastById(toast.id);
        }
    };
    const secondaryAction = (toast) => {
        if (toast.options?.secondaryActionButtonCallback) {
            toast.options.secondaryActionButtonCallback();
            closeToastById(toast.id);
        }
    };
    const closeToast = (toast) => {
        closeToastById(toast.id);
    };
    const closeToastById = (toastId) => {
        toastQueue.remove(toastId);
    };
    const closeAllToasts = () => {
        toastQueue.clear();
    };
    const legacyAddNotice = function (message, cssClass, timeout, undoCallback) {
        const type = cssClass === "rc-error" ? ToastType.ERROR : ToastType.SUCCESS;
        const options = { timeout };
        if (undoCallback) {
            options.actionButtonLabel = i18n.t("Undo");
            options.actionButtonCallback = undoCallback;
        }
        addToast(type, message, options);
    };
    onMounted(() => {
        toastQueue.clear();
    });
    return {
        toastList,
        addToast,
        addSuccessNotice,
        addErrorNotice,
        addDeleteNotice,
        addFatalErrorNotice,
        addFatalSaveErrorNotice,
        toastHover,
        toastClick,
        primaryAction,
        secondaryAction,
        closeToast,
        closeToastById,
        closeAllToasts,
        legacyAddNotice
    };
});
